/* Static data for the Development Simulator. */

export type ProjectKey = "duplex" | "mixed" | "luxury";

export interface DevProject {
  key: ProjectKey;
  name: string;
  type: string;
  askingPrice: number;
  rehabBuildCost: number;
  expectedValue: number;
  timelineMonths: number;
  difficulty: "Low" | "Medium" | "High";
  permitRisk: "Medium" | "High";
  cashNeededLow: number;
  cashNeededHigh: number;
  surfaceSummary: string;
  fits: string;
  teaches: string;
  baseRisk: number;
  hardCostPct: number; // share of (asking + build) that is hard build
}

export const PROJECTS: DevProject[] = [
  {
    key: "duplex",
    name: "Duplex Conversion",
    type: "Small residential redevelopment",
    askingPrice: 120_000,
    rehabBuildCost: 180_000,
    expectedValue: 420_000,
    timelineMonths: 8,
    difficulty: "Low",
    permitRisk: "Medium",
    cashNeededLow: 75_000,
    cashNeededHigh: 105_000,
    surfaceSummary: "Older two-unit converted into a modern duplex. Listing looks clean.",
    fits: "Smaller capital base, first development project.",
    teaches: "Small projects still carry real delay and repair risk.",
    baseRisk: 25,
    hardCostPct: 0.60,
  },
  {
    key: "mixed",
    name: "Mixed-Use Retail",
    type: "Commercial + residential redevelopment",
    askingPrice: 280_000,
    rehabBuildCost: 450_000,
    expectedValue: 950_000,
    timelineMonths: 14,
    difficulty: "Medium",
    permitRisk: "High",
    cashNeededLow: 180_000,
    cashNeededHigh: 255_000,
    surfaceSummary: "Retail on ground floor, apartments above. Pre-leasing in talks.",
    fits: "Balancing commercial tenants with residential cash flow.",
    teaches: "Bigger upside, more moving parts: zoning + dual approvals.",
    baseRisk: 40,
    hardCostPct: 0.62,
  },
  {
    key: "luxury",
    name: "Luxury Condo Build",
    type: "New construction",
    askingPrice: 500_000,
    rehabBuildCost: 1_200_000,
    expectedValue: 2_400_000,
    timelineMonths: 24,
    difficulty: "High",
    permitRisk: "High",
    cashNeededLow: 425_000,
    cashNeededHigh: 595_000,
    surfaceSummary: "Ground-up condo build in a transitioning luxury corridor.",
    fits: "Stress-testing leverage + delays across a long timeline.",
    teaches: "Big projected profit disappears fast when delays + rates stack.",
    baseRisk: 60,
    hardCostPct: 0.70,
  },
];

/* ─── Documents per project ─── */

export interface DocumentRedFlag {
  id: string;
  title: string;
  body: string;
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

export const PROJECT_DOCUMENTS: Record<ProjectKey, DocumentRedFlag[]> = {
  duplex: [
    {
      id: "duplex-inspection",
      title: "Inspection Summary",
      body: "Roof age 22 years. Galvanized supply lines throughout. Knob-and-tube wiring in attic. Basement shows water staining 30in up the foundation wall. No active leak observed at inspection.",
      question: "What is the biggest red flag in this inspection?",
      options: [
        "Knob-and-tube wiring and water staining suggest costly hidden repairs.",
        "The roof age is fine because it was not actively leaking.",
        "There are no red flags — the inspection looks clean.",
        "Galvanized pipes always extend a property's life.",
      ],
      correctIndex: 0,
      explanation: "Old wiring forces rewiring for code; water staining hints at recurring moisture. Both can blow the budget if unbudgeted.",
    },
    {
      id: "duplex-contractor",
      title: "Contractor Estimate",
      body: "Labor: $92,000. Materials: $74,500. Timeline: 6 months. No contingency line. No allowance for permit revisions. Payment: 50% upfront.",
      question: "What is missing from this contractor estimate?",
      options: [
        "It has labor and materials but no contingency or permit allowance.",
        "It includes every possible cost and a guarantee against delays.",
        "It is too detailed and over-specified.",
        "It properly accounts for change orders.",
      ],
      correctIndex: 0,
      explanation: "No contingency line and 50% upfront is a red flag. Real estimates include change-order and permit allowances.",
    },
    {
      id: "duplex-comps",
      title: "Sale Comps Snapshot",
      body: "Comparable duplexes nearby sold for $385k–$430k over the last 12 months. Days on market trending up from 21 → 58. Two recent comps had price reductions.",
      question: "What does this comp data signal?",
      options: [
        "The market is softening — be cautious about the $420k target.",
        "Demand is rising — push the price above comps.",
        "Comps are irrelevant to development projects.",
        "Price reductions are a positive market signal.",
      ],
      correctIndex: 0,
      explanation: "Rising days-on-market and price cuts mean the headline value may not hold by the time you exit.",
    },
  ],
  mixed: [
    {
      id: "mixed-zoning",
      title: "Zoning Note",
      body: "Site is zoned MU-2 (mixed use). Proposed retail concept may require a conditional use permit. City planner notes a similar application took 9 months last year.",
      question: "What is the biggest concern in this zoning note?",
      options: [
        "Conditional use permits can add many months and may be denied.",
        "MU-2 zoning automatically approves retail.",
        "Past timelines never affect new applications.",
        "Zoning is irrelevant once you own the land.",
      ],
      correctIndex: 0,
      explanation: "Conditional approvals are slow and uncertain. Carrying costs run the entire time.",
    },
    {
      id: "mixed-leasing",
      title: "Anchor Tenant Letter of Intent",
      body: "Prospective anchor tenant signed a non-binding LOI for ground floor retail. Rent terms TBD. Tenant requests right to cancel if buildout exceeds 12 months.",
      question: "What's the red flag in this LOI?",
      options: [
        "It's non-binding and includes a cancellation clause tied to your timeline.",
        "LOIs are always binding lease contracts.",
        "The tenant has guaranteed rent for 10 years.",
        "There is no risk because the rent is set.",
      ],
      correctIndex: 0,
      explanation: "Non-binding LOIs can vanish. The cancellation clause means a delay also costs you the tenant.",
    },
    {
      id: "mixed-termsheet",
      title: "Lender Term Sheet",
      body: "Construction loan at 9.5% interest-only. 70% LTC. Requires anchor tenant signed lease before final draw. Personal guarantee required.",
      question: "Which term is the biggest risk?",
      options: [
        "Final draw is contingent on a signed anchor lease — which is still uncertain.",
        "Interest-only payments are unusual and unsafe.",
        "Personal guarantees never affect developers.",
        "70% LTC is unusually low for construction loans.",
      ],
      correctIndex: 0,
      explanation: "If the anchor lease never gets signed, the lender can refuse the final draw — and you stall mid-project.",
    },
  ],
  luxury: [
    {
      id: "luxury-market",
      title: "Market Sale Comps",
      body: "Recent luxury condo comps: $1.9M–$2.5M. Inventory up 40% YoY. Top brokers report buyer hesitation in the $2M+ band. Average days on market: 142. The headline expected value is $2.4M.",
      question: "What does this market data suggest?",
      options: [
        "The luxury market is softening — the $2.4M target may not hold over a 24-month build.",
        "Rising inventory means rising prices.",
        "Long days on market means buyers are competing aggressively.",
        "Market comps don't matter for new construction.",
      ],
      correctIndex: 0,
      explanation: "Soft luxury markets punish long timelines. The longer you build, the more market risk stacks against your exit price.",
    },
    {
      id: "luxury-contractor",
      title: "Contractor Estimate",
      body: "Hard cost estimate: $1.2M, but escalators tied to steel + concrete index. 24-month construction. Change-order rate on similar projects: 18% of base.",
      question: "What's the biggest hidden cost here?",
      options: [
        "Material-index escalators plus an 18% change-order rate can blow the budget.",
        "The base estimate is the maximum you'll pay.",
        "Change orders never affect total cost.",
        "Steel and concrete prices are always flat.",
      ],
      correctIndex: 0,
      explanation: "Escalators + change orders on a long luxury build commonly add 15–25% to the headline number.",
    },
    {
      id: "luxury-permits",
      title: "Permit Requirement Note",
      body: "Requires: site plan approval, environmental review, traffic study, neighbor notification, design review board. Estimated 6–10 months pre-construction.",
      question: "What does this permit list imply?",
      options: [
        "Pre-construction alone could take nearly a year — carrying costs apply immediately.",
        "Permits are quick once you start construction.",
        "Carrying costs only begin after construction starts.",
        "Design review boards usually approve plans within a week.",
      ],
      correctIndex: 0,
      explanation: "Carrying costs (taxes, insurance, debt) run from acquisition — every month of pre-construction is paid by you.",
    },
  ],
};

/* ─── Lender term sheets ─── */

export type LenderKey = "bank" | "balanced" | "private" | "aggressive";

export interface LenderOffer {
  key: LenderKey;
  name: string;
  ltc: number;
  rate: number;
  approvalDays: number;
  bullets: string[];
  riskDelta: number;
}

export const LENDERS: LenderOffer[] = [
  { key: "bank", name: "Conservative Bank Loan", ltc: 0.65, rate: 0.075, approvalDays: 60,
    bullets: ["Lowest interest", "More equity required", "Slow approval"], riskDelta: -20 },
  { key: "balanced", name: "Balanced Lender", ltc: 0.70, rate: 0.085, approvalDays: 35,
    bullets: ["Middle-of-market terms", "Moderate carry", "Standard underwriting"], riskDelta: 0 },
  { key: "private", name: "Private Lender", ltc: 0.80, rate: 0.12, approvalDays: 14,
    bullets: ["Less equity required", "Higher interest carry", "Faster close"], riskDelta: 20 },
  { key: "aggressive", name: "Aggressive Debt Stack", ltc: 0.85, rate: 0.14, approvalDays: 14,
    bullets: ["Thinnest equity cushion", "Highest blended rate", "Cash crunch likely if delayed"], riskDelta: 35 },
];

/* ─── Contractor options ─── */

export type ContractorKey = "cheap" | "balanced" | "premium" | "informal";

export interface ContractorOffer {
  key: ContractorKey;
  name: string;
  costMult: number;       // multiplier on hard costs
  timelineDelta: number;  // months added (negative shortens)
  riskDelta: number;
  qualityDelta: number;   // affects final value pct
  bullets: string[];
}

export const CONTRACTORS: ContractorOffer[] = [
  { key: "cheap", name: "Cheapest Bid", costMult: 0.92, timelineDelta: 1, riskDelta: 25, qualityDelta: -0.03,
    bullets: ["Saves money upfront", "Higher rework risk", "Schedule slips common"] },
  { key: "balanced", name: "Balanced Contractor", costMult: 1.0, timelineDelta: 0, riskDelta: 0, qualityDelta: 0,
    bullets: ["Reliable timeline", "Average risk", "Predictable execution"] },
  { key: "premium", name: "Premium Contractor", costMult: 1.12, timelineDelta: -1, riskDelta: -20, qualityDelta: 0.05,
    bullets: ["Higher hard cost", "Lower delay risk", "Better finish quality"] },
  { key: "informal", name: "Friend-of-a-Friend", costMult: 0.85, timelineDelta: 2, riskDelta: 40, qualityDelta: -0.08,
    bullets: ["Cheap but informal", "No real contract", "High dispute & inspection risk"] },
];

/* ─── Permit strategies ─── */

export type PermitKey = "complete" | "quick" | "expeditor" | "early";

export interface PermitOption {
  key: PermitKey;
  name: string;
  cost: number;
  timelineDelta: number;
  riskDelta: number;
  bullets: string[];
}

export const PERMIT_OPTIONS: PermitOption[] = [
  { key: "complete", name: "Submit complete package", cost: 1_500, timelineDelta: 0, riskDelta: -15,
    bullets: ["Higher upfront cost", "Smoother review", "Fewer revision cycles"] },
  { key: "quick", name: "Submit quickly, missing details", cost: 0, timelineDelta: 1, riskDelta: 25,
    bullets: ["Saves time now", "Revisions trigger delays", "Reviewers lose confidence"] },
  { key: "expeditor", name: "Hire permit expeditor", cost: 4_000, timelineDelta: -1, riskDelta: -20,
    bullets: ["Highest upfront cost", "Faster cycle time", "Lower delay risk"] },
  { key: "early", name: "Start work before approval", cost: 0, timelineDelta: 0, riskDelta: 45,
    bullets: ["Possible speed gain", "Stop-work order risk", "Penalties + fines"] },
];

/* ─── Construction event responses ─── */

export type ResponseKey = "use_contingency" | "borrow" | "cut_scope" | "pause";

export const RESPONSES = [
  { key: "use_contingency" as ResponseKey, name: "Use contingency reserve",
    bullets: ["Best if you have buffer", "Protects timeline"] },
  { key: "borrow" as ResponseKey, name: "Borrow short-term",
    bullets: ["Keeps project moving", "Adds debt + interest"] },
  { key: "cut_scope" as ResponseKey, name: "Cut scope",
    bullets: ["Saves cash", "Final value drops"] },
  { key: "pause" as ResponseKey, name: "Pause the project",
    bullets: ["Prevents overspend now", "Timeline + carrying cost grow"] },
];

/* ─── Cash crunch recovery options ─── */

export type CrunchKey = "emergency_debt" | "cut_scope" | "fire_sale" | "partner" | "pause";
export const CRUNCH_OPTIONS = [
  { key: "emergency_debt" as CrunchKey, name: "Take expensive emergency debt",
    bullets: ["18% rate", "Project continues", "Profit takes a major hit"] },
  { key: "cut_scope" as CrunchKey, name: "Cut project scope hard",
    bullets: ["Lowers cost now", "Final value drops 10%"] },
  { key: "partner" as CrunchKey, name: "Bring in an equity partner",
    bullets: ["Cash injection", "Give up 30% of upside"] },
  { key: "fire_sale" as CrunchKey, name: "Sell early at a discount",
    bullets: ["Exit now", "Sells at 80% of expected value"] },
];

/* ─── Glossary (in-module Key Terms) ─── */

export const GLOSSARY: Array<{ term: string; def: string }> = [
  { term: "Loan", def: "Money borrowed from a lender that must be repaid with interest." },
  { term: "Equity", def: "The cash you put into a project that isn't borrowed." },
  { term: "LTV (Loan-to-Value)", def: "Loan ÷ estimated finished value. Higher LTV = more leverage." },
  { term: "LTC (Loan-to-Cost)", def: "Loan ÷ total project cost. Common lender metric for construction." },
  { term: "Interest rate", def: "The annual percentage charged on borrowed money." },
  { term: "Carrying cost", def: "Ongoing monthly costs (interest, taxes, insurance, utilities) while you hold a project." },
  { term: "Contingency reserve", def: "A buffer of cash for surprises — usually 5–15% of project cost." },
  { term: "Leverage", def: "Using borrowed money to amplify returns. Also amplifies losses." },
  { term: "Hard costs", def: "Physical construction costs — labor, materials, site work." },
  { term: "Soft costs", def: "Non-construction costs — permits, design, legal, financing fees." },
  { term: "Exit strategy", def: "The plan to convert a finished project into cash or income." },
  { term: "Rental cash flow", def: "Rent received minus debt, repairs, vacancy, taxes, insurance, and management." },
  { term: "Vacancy", def: "Months a rental unit sits empty — lost rent that still costs you carrying expenses." },
  { term: "Refinance", def: "Replacing the existing loan with a new one, often to pull equity out or lower payments." },
  { term: "Appraisal", def: "A professional estimate of property value, usually required for refinancing." },
  { term: "DSCR", def: "Debt Service Coverage Ratio — net rental income ÷ debt payment. Above 1.20 is generally healthy." },
  { term: "Selling costs", def: "Agent commissions, closing fees, and transfer taxes paid when you sell." },
  { term: "Return on equity", def: "Profit ÷ cash invested. Measures how hard your own money worked." },
  { term: "Change order", def: "A contractor request to change scope, cost, or timeline mid-project." },
  { term: "Cash crunch", def: "When cash on hand falls below what the project needs to continue." },
];

/* ─── Budget categories ─── */

export type BudgetCategory =
  | "hard_costs" | "soft_costs" | "permits_legal"
  | "carrying_costs" | "contingency" | "sale_prep";

export const BUDGET_CATEGORIES: Array<{
  key: BudgetCategory; label: string; suggested: number; help: string;
  underWarn: number; warnText: string;
}> = [
  { key: "hard_costs", label: "Hard costs", suggested: 55, help: "Physical construction — labor + materials.", underWarn: 40, warnText: "Build quality and timeline suffer." },
  { key: "soft_costs", label: "Soft costs", suggested: 8, help: "Design, engineering, legal, financing fees.", underWarn: 4, warnText: "Designs get cut corners; legal exposure rises." },
  { key: "permits_legal", label: "Permits / legal", suggested: 5, help: "City permits, zoning, attorney fees.", underWarn: 3, warnText: "Permit delay probability increases." },
  { key: "carrying_costs", label: "Carrying costs", suggested: 10, help: "Interest, taxes, insurance, utilities during build.", underWarn: 6, warnText: "Cash crunch risk rises." },
  { key: "contingency", label: "Contingency", suggested: 10, help: "Reserve for surprises.", underWarn: 5, warnText: "Any surprise becomes a crisis." },
  { key: "sale_prep", label: "Sale / lease prep", suggested: 12, help: "Marketing, staging, tenant fit-out.", underWarn: 6, warnText: "Exit value drops; slower lease-up." },
];